import { HttpClient } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, retry, shareReplay } from 'rxjs/operators';

import { GamePeriodModel } from '../models/game-period.model';
import { GameModel } from '../models/game.model';

const API_ENDPOINT = '/api/game/periods/';

@Injectable({
  providedIn: 'root',
})
export class GamePeriodService implements OnInit {

  public periodSwitched: Subject<number> = new Subject<number>();
  public periodSwitchedObj: Subject<GamePeriodModel> = new Subject<GamePeriodModel>();
  public currentGamePeriod: GamePeriodModel;
  public activeGamePeriodSubject: Subject<GamePeriodModel> = new Subject<GamePeriodModel>();
  public endGameTimestamp: number;

  private cachedCurrentGamePeriodRequest: Observable<GamePeriodModel>;
  private gamePeriods: Observable<GamePeriodModel[]>;

  /**
   * Constructor
   *
   * @param http [The http client]
   */
  public constructor(private http: HttpClient) {
    this.currentGamePeriod = new GamePeriodModel();
  }

  /**
   * Initialize the service
   *
   * @return void
   */
  public ngOnInit(): void {
    // this.currentGamePeriod = this.getCurrentGamePeriod();
  }

  /**
   * Gets an observable that resolves to the current set game periods
   *
   * @param {boolean} reload
   *
   * @return {Observable<GamePeriodModel[]>}
   */
  public getGamePeriods(reload: boolean = false): Observable<GamePeriodModel[]> {
    if (!this.gamePeriods || reload) {
      this.gamePeriods = this.loadGamePeriods().pipe(
        shareReplay(1),
      );
    }

    return this.gamePeriods;
  }

  /**
   * Gets a list of game periods and the active game period
   *
   * @returns void
   */
  public loadGamePeriods(): Observable<any> {
    return this.http.get(API_ENDPOINT + 'get/list')
    .pipe(
      retry(3),
      map(
        (response: { periods: GamePeriodModel[], active: GamePeriodModel, end_game_timestamp: number }) => {
          if (response.active !== null) {
            this.setCurrentGamePeriod(response.active);
          }
          this.endGameTimestamp = response.end_game_timestamp;

          return response.periods;
        },
      ),
    );
  }

  /**
   * Fetch the active game Period from the api
   *
   * @return Observable<GamePeriodModel>
   */
  public getActiveGamePeriod(): Observable<GamePeriodModel> {
    return this.http.get(API_ENDPOINT + 'get/active-period').pipe(
      map((response: { error: string, items: { gamePeriod: GamePeriodModel } }) => {
        this.activeGamePeriodSubject.next(response.items.gamePeriod);
        return response.items.gamePeriod;
      }),
      catchError(() => {
        return throwError('The operation could not be completed. Please try again');
      }),
    );
  }

  /**
   * Loads the game for the app, only after being logged in.
   *
   * @return {Observable<GameModel>}
   */
  public loadCurrentGamePeriod(forceReload: boolean = false): Observable<GamePeriodModel> {

    if (forceReload) {
      this.cachedCurrentGamePeriodRequest = null;
    }

    if (!this.cachedCurrentGamePeriodRequest) {
      this.cachedCurrentGamePeriodRequest = this.http.get(API_ENDPOINT + 'get/active-period')
        .pipe(
          map((response: { items: { gamePeriod: GamePeriodModel } }) => {
            this.currentGamePeriod = response.items.gamePeriod;
            return this.currentGamePeriod;
          }),
          shareReplay(1),
        );
    }

    return this.cachedCurrentGamePeriodRequest;
  }

  /**
   * Set the current GamePeriod
   *
   * @param gamePeriod
   * @return void
   */
  public setCurrentGamePeriod(gamePeriod: GamePeriodModel): void {
    this.currentGamePeriod = gamePeriod;
    this.activeGamePeriodSubject.next(gamePeriod);
    this.periodSwitched.next(gamePeriod.number);
  }

  /**
   * Get the current GamePeriod
   *
   * @return GamePeriodModel
   */
  public getCurrentGamePeriod(): GamePeriodModel {
    if (this.currentGamePeriod.iHash === null) {
      this.getActiveGamePeriod().subscribe(
        (gamePeriod: GamePeriodModel) => {
          this.currentGamePeriod = gamePeriod;
        },
      );
    }
    return this.currentGamePeriod;
  }
}
